import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftRight } from 'lucide-react';
import axios from 'axios';
import logger from '@/utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeIn = { initial: { opacity: 0 }, whileInView: { opacity: 1 }, viewport: { once: true } };
const fadeInUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };
const halfSec = { duration: 0.5 };
const viewportOnce = { once: true };

const cardFadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.4, delay: i * 0.1 } }),
};

function GalleryCard({ project, index }) {
  return (
    <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce}
      variants={cardFadeUp} custom={index} className="gallery-card border border-black overflow-hidden group" data-testid={`gallery-card-${index}`}>
      <div className="relative h-56 sm:h-64 overflow-hidden">
        <img src={project.before_image} alt={`electrician Nashville TN - ${project.title} before`} className="w-full h-full object-cover" loading="lazy" />
        <img src={project.after_image} alt={`electrician Nashville TN - ${project.title} after`} className="gallery-after absolute inset-0 w-full h-full object-cover" loading="lazy" />
        <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/70 text-white px-3 py-1">
          <ArrowLeftRight className="w-3 h-3" />
          <span className="font-body text-xs uppercase tracking-wider">Hover to compare</span>
        </div>
        <span className="absolute top-4 right-4 bg-yellow-400 text-black font-heading font-bold text-xs px-3 py-1 uppercase tracking-wider">{project.category}</span>
      </div>
      <div className="p-6">
        <h3 className="font-heading text-base font-bold uppercase tracking-tight mb-2">{project.title}</h3>
        <p className="font-body text-sm text-zinc-500 leading-relaxed">{project.description}</p>
      </div>
    </motion.div>
  );
}

export default function GallerySection() {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await axios.get(`${API}/gallery`);
        setProjects(res.data);
      } catch (err) {
        logger.error('Gallery fetch failed:', err);
      }
    };
    fetchGallery();
  }, []);

  if (!projects.length) return null;

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 sm:py-32 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.p {...fadeIn} className="font-body text-xs uppercase tracking-[0.2em] text-zinc-400 mb-3">Recent Projects</motion.p>
        <motion.h2 {...fadeInUp} transition={halfSec} className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight uppercase mb-12">Before & After Gallery</motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {projects.map((p, i) => <GalleryCard key={p.id} project={p} index={i} />)}
        </div>
      </div>
    </section>
  );
}
